import { useEffect, useRef, useState, useCallback } from "react";
import Header from "../../../components/layout/Header";
import SelectSiteHeader from "../../../components/layout/SelectSiteHeader";
import { DAP_URL, ENV_TYPE } from "../../../shared/config";
import { Text } from "src/components/ui/Text";
import { useDempStore } from "src/context/stores";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const MicroServiceDap = () => {
  const iframeRef = useRef(null);
  const firstRender = useRef(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [iframeIsLoading, setIframeIsLoading] = useState(true);
  const [companyId, plantId] = useDempStore((state) => [
    state.selectedCompanies,
    state.plantId,
  ]);
  const [language, setLanguage] = useState("en");

  //   query param for direct to dap emission transaction by id
  const service = searchParams.get("service");
  const id = searchParams.get("id");
  const param = searchParams.get("dap_token");
  const getLanguage = location ? location.pathname.split("/")[1] : "en";

  const [urlReady, setUrlReady] = useState(false);

  //   get path name from session storage
  const pathname = sessionStorage.getItem("pathname");

  //   encode base64
  const encodeBase64 = (str) => {
    return btoa(str);
  };

  //   decode base64
  const decodeBase64 = (base64) => {
    return atob(base64);
  };

  //   set url iframe ready open when company id and plant id is ready
  useEffect(() => {
    if (companyId || plantId) {
      setUrlReady(true);

      //   send company id and plant id to iframe
      iframeRef?.current?.contentWindow.postMessage(
        { companyId, plantId, language: getLanguage },
        ENV_TYPE === "dev" ? "http://localhost:8080" : window?.location?.origin
      );
    }
  }, [companyId, plantId, language, getLanguage]);

  // set pathname to session storage when it changes
  useEffect(() => {
    const searchParams = location.search.replace("?", "");

    if (firstRender.current) {
      if (searchParams) {
        const decodedPathname = decodeBase64(param);

        sessionStorage.setItem("pathname", decodedPathname);
      }

      //   get language from local pathname
      const getLocalStorageLanguage = localStorage.getItem("i18nextLng");

      if (getLocalStorageLanguage) {
        setLanguage(getLocalStorageLanguage);
      }

      firstRender.current = false;
      return;
    }
  }, [location.pathname, location.search, param]);

  const iframeUrlDev = `http://localhost:8080/dap/${language}/${
    !firstRender.current && pathname ? `${pathname}` : ""
  }${companyId ? `?company_id=${companyId}` : ""}${
    plantId ? `&plant_id=${plantId}` : ""
  }${service ? `&service=${service}` : ""}${id ? `&id=${id}` : ""}`;

  //   const iframeUrlDev = `http://localhost:8080/dap/en/${
  //     !firstRender.current && pathname ? `${pathname}` : "dashboard"
  //   }${service ? `?service=${service}` : ""}${id ? `?id=${id}` : ""}`;

  const iframeUrl = `${DAP_URL}/${language}/${
    !firstRender.current && pathname ? `${pathname}` : "dashboard"
  }${service ? `?service=${service}` : ""}${id ? `?id=${id}` : ""}`;

  const urlIframe = ENV_TYPE === "dev" ? iframeUrlDev : iframeUrl;

  const handleIframeMessage = useCallback(
    (event) => {
      // Check the origin of the message
      if (!urlIframe.includes(event.origin)) return;

      // Check if the message url the same page from the iframe set the params to the parent page
      if (typeof event?.data === "object") {
        const { message, value } = event?.data;

        const newPath = value ? encodeBase64(value) : "";
        const pathname = location?.pathname;

        if (message === "url" && newPath) {
          const updatedPath = `${pathname}?dap_token=${newPath}`;

          navigate(updatedPath, {});
        }
      }

      // Check if the reload message is from the iframe
      if (event?.data === "reload") {
        window.location.reload();
      }
    },
    [location?.pathname, navigate, urlIframe]
  );

  useEffect(() => {
    // Add event listener for messages from iframe
    window.addEventListener("message", handleIframeMessage);
    return () => {
      // Cleanup event listener on component unmount
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [handleIframeMessage]);

  return (
    <div className="flex flex-col w-full h-[100vh]">
      <div>
        <Header pageTitle="DAP" showSubMenu={false}>
          <SelectSiteHeader />
        </Header>
      </div>

      <div className="flex relative flex-col w-full grow">
        {urlReady && (
          <iframe
            ref={iframeRef}
            src={urlIframe}
            className="w-full grow"
            height="100%"
            title="DAP System"
            allow="geolocation"
            onLoad={() => setIframeIsLoading(false)}
          />
        )}

        {iframeIsLoading && (
          <div className="flex absolute justify-center items-center w-full h-full">
            <Text variant={"medium-18"} className={"text-tertiary"}>
              Loading...
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

export default MicroServiceDap;
